import { parseApiDate } from '@/lib/dates';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import { getApiErrorMessage, usePartnerAuthStore } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusPill } from '@/components/groups/StatusPill';
import { ApplyPromoCodeDialog } from '@/components/groups/ApplyPromoCodeDialog';
import { formatPlanPrice, formatSeats, type GroupDetail } from '@/types/groups';
import { describeDiscount } from '@/types/promoCodes';

function formatDate(iso: string | null): string {
  return iso ? parseApiDate(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set';
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm">{value}</div>
    </div>
  );
}

export function GroupDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { partner } = usePartnerAuthStore();
  const isAdmin = partner?.role === 'admin';
  // Keyed by group: the page instance is reused when the route moves to another
  // group, and the dialog must not follow it.
  const [applyOpenFor, setApplyOpenFor] = useState<string | null>(null);
  const isApplyOpen = applyOpenFor === uuid;

  // Only the latest request may set state: a reload can overtake the mount
  // load, and a response for the previous uuid must not land on the next one.
  const latestRequest = useRef(0);
  const dropInFlight = useCallback(() => {
    latestRequest.current++;
  }, []);

  const loadGroup = useCallback(
    (isReload = false) => {
      const request = ++latestRequest.current;
      const isCurrent = () => request === latestRequest.current;

      return api
        .get(`/groups/${uuid}`)
        .then((response) => {
          if (isCurrent()) setGroup(response.data.data);
        })
        .catch((error) => {
          if (!isCurrent()) return;

          if (isReload) {
            // The apply already succeeded, so the group on screen stays and
            // the failure is only that it is now out of date.
            toast.error('Promo code applied, but the page could not refresh. Reload to see the change.', {
              id: 'group-detail-error',
            });
            return;
          }

          setGroup(null);
          // Laravel's 404 body is "No query results for model [App\Models\Group]
          // <uuid>", which is not something to put in front of a support agent.
          const message =
            error?.response?.status === 404 ? 'Group not found.' : getApiErrorMessage(error);
          toast.error(message, { id: 'group-detail-error' });
        })
        .finally(() => {
          if (isCurrent()) setIsLoading(false);
        });
    },
    [uuid],
  );

  useEffect(() => {
    if (!uuid) {
      return;
    }

    void loadGroup();

    return () => {
      // Leaving a group (or the page) drops its in-flight response and closes
      // its dialog, so coming back to it later does not reopen it.
      dropInFlight();
      setApplyOpenFor(null);
    };
  }, [uuid, loadGroup, dropInFlight]);

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!group) {
    return (
      <div className="space-y-4">
        <Link className="inline-flex items-center gap-2 text-sm hover:underline" to="/groups">
          <ArrowLeft className="h-4 w-4" />
          Back to groups
        </Link>
        <p className="text-sm text-muted-foreground">This group could not be loaded.</p>
      </div>
    );
  }

  const { subscription } = group;
  const appliedPromoCode = subscription?.appliedPromoCode ?? null;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Link className="inline-flex items-center gap-2 text-sm hover:underline" to="/groups">
          <ArrowLeft className="h-4 w-4" />
          Back to groups
        </Link>
        <h1 className="font-display text-3xl font-bold">{group.name}</h1>
        <p className="text-muted-foreground">
          {group.code} &middot; created {formatDate(group.createdAt)}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Group</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Country" value={group.countryName ?? 'Not set'} />
            <Field label="District" value={group.district ?? 'Not set'} />
            <Field label="Address" value={group.address ?? 'Not set'} />
            <Field label="Founded" value={formatDate(group.foundedAt)} />
            <Field label="Registered" value={group.isRegistered ? 'Yes' : 'No'} />
            <Field label="Registration number" value={group.registrationNumber ?? 'Not set'} />
            {group.description && (
              <div className="sm:col-span-2">
                <Field label="Description" value={group.description} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Subscription</CardTitle>
            <Link className="text-sm font-medium text-primary hover:underline" to={`/invoices?group=${group.uuid}`}>
              View invoices
            </Link>
          </CardHeader>
          <CardContent>
            {subscription ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Plan" value={subscription.tierName ?? 'Not set'} />
                <Field label="Status" value={<StatusPill status={subscription.status} />} />
                <Field label="Price" value={formatPlanPrice(subscription)} />
                <Field label="Members" value={formatSeats(group.memberCount, subscription.memberLimit)} />
                <Field label="Current period ends" value={formatDate(subscription.endsAt)} />
                <Field
                  label="Trial ends"
                  value={subscription.trialEndsAt ? formatDate(subscription.trialEndsAt) : 'Not on trial'}
                />
                <Field
                  label="Promo code"
                  value={
                    appliedPromoCode ? (
                      <span>
                        <span className="font-mono font-medium">{appliedPromoCode.code}</span>
                        <span className="block text-xs text-muted-foreground">{describeDiscount(appliedPromoCode)}</span>
                      </span>
                    ) : isAdmin ? (
                      <Button variant="outline" size="sm" onClick={() => setApplyOpenFor(uuid ?? null)}>
                        Apply promo code
                      </Button>
                    ) : (
                      'None'
                    )
                  }
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">This group has no subscription on record.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Members ({group.memberCount})</CardTitle>
        </CardHeader>
        <CardContent>
          {group.members.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nobody has joined this group yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.members.map((member, index) => (
                    // Two same-named members can join in the same second via a
                    // bulk import, so the index is part of the key.
                    <TableRow key={`${member.name}-${member.joinedAt}-${index}`}>
                      <TableCell className="font-medium">{member.name}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {member.role}
                        {member.isAdmin && (
                          <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                            Admin
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(member.joinedAt)}</TableCell>
                      {/* Contact details reach this surface for officers only. */}
                      <TableCell className={member.email ? '' : 'text-muted-foreground'}>
                        {member.email ?? 'Not shown'}
                      </TableCell>
                      <TableCell
                        className={member.phone ? 'whitespace-nowrap' : 'whitespace-nowrap text-muted-foreground'}
                      >
                        {member.phone ?? 'Not shown'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {isApplyOpen && subscription && (
        <ApplyPromoCodeDialog
          open
          onOpenChange={(open) => setApplyOpenFor(open ? uuid ?? null : null)}
          groupUuid={group.uuid}
          groupName={group.name}
          amendableInvoices={subscription.amendableInvoices}
          onApplied={() => void loadGroup(true)}
        />
      )}
    </div>
  );
}
